/**
 * Locale context + `useTranslation` hook for TanStack Start SSR.
 *
 * The active locale is derived deterministically from the URL on both server
 * and client (the `/de` prefix), so the provider just needs the locale handed
 * to it - there is no client-only detection that could mismatch SSR markup.
 */
import { createContext, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import { DEFAULT_LOCALE } from './config'
import type { Locale } from './config'
import { getDictionary, translate } from './dictionary'
import type { Dictionary, TranslationKey, TranslationValues } from './dictionary'
import {
  equivalentPath as equivalentPathFor,
  localizedPath as localizedPathFor,
} from './routes'
import type { RouteKey, RouteParams } from './routes'
import { formatDate as formatDateFor } from './format'

export type TranslationApi = {
  locale: Locale
  /** Translate a dot-path key with optional `{token}` interpolation. */
  t: (key: TranslationKey, values?: TranslationValues) => string
  /** Direct, typed access to the active dictionary (for arrays/objects). */
  dict: Dictionary
  /** Build a locale-prefixed path for a logical route. */
  path: (key: RouteKey, params?: RouteParams) => string
  /** Map a pathname to its equivalent in another locale. */
  equivalentPath: (pathname: string, target: Locale) => string
  /** Locale-aware date formatting. */
  formatDate: (
    value: string | number | Date,
    options?: Intl.DateTimeFormatOptions,
  ) => string
}

const LocaleContext = createContext<Locale>(DEFAULT_LOCALE)

export function LocaleProvider({
  locale,
  children,
}: {
  locale: Locale
  children: ReactNode
}) {
  return (
    <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>
  )
}

export function useLocale(): Locale {
  return useContext(LocaleContext)
}

export function useTranslation(): TranslationApi {
  const locale = useLocale()
  return useMemo(
    () => ({
      locale,
      t: (key, values) => translate(locale, key, values),
      dict: getDictionary(locale),
      path: (key, params) => localizedPathFor(key, locale, params),
      equivalentPath: (pathname, target) => equivalentPathFor(pathname, target),
      formatDate: (value, options) => formatDateFor(value, locale, options),
    }),
    [locale],
  )
}
